import React from 'react';
import { Lock } from 'lucide-react';

/**
 * StripeTrustBanner — premium trust signal for moments right before payment redirect.
 *
 * Variants:
 *  - "inline": compact horizontal pill (for register page above CTA)
 *  - "card":   full card with explanation (for use in pricing pages)
 *
 * Visual: official Stripe purple accent + lock icon + plain trust copy.
 * No marketing fluff — just the facts that calm the user.
 */

const StripeWordmark = ({ className = '' }) => (
  // Official Stripe wordmark — simplified text logo in their official purple
  <span
    className={`font-bold tracking-tight ${className}`}
    style={{ color: '#635BFF', fontFamily: '"Helvetica Neue", Arial, sans-serif' }}
  >
    stripe
  </span>
);

export const StripeTrustBanner = ({ variant = 'inline', className = '', dark = false }) => {
  if (variant === 'card') {
    return (
      <div
        data-testid="stripe-trust-card"
        className={`rounded-2xl border ${
          dark
            ? 'border-white/10 bg-white/[0.03]'
            : 'border-slate-200 bg-slate-50/50'
        } p-4 sm:p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
              dark ? 'bg-white/[0.06]' : 'bg-white border border-slate-200'
            }`}
          >
            <Lock className={`w-4 h-4 ${dark ? 'text-white/70' : 'text-slate-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className={`text-[13px] font-bold flex items-center gap-1.5 ${
                dark ? 'text-white' : 'text-slate-900'
              }`}
            >
              Secure payment by <StripeWordmark className="text-[14px]" />
            </div>
            <p
              className={`mt-1 text-[12px] leading-relaxed ${
                dark ? 'text-white/55' : 'text-slate-500'
              }`}
            >
              Used by Amazon, Shopify, Uber, Lyft & millions of businesses worldwide.
              NoFee never sees or stores your card details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // inline pill
  return (
    <div
      data-testid="stripe-trust-pill"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${
        dark
          ? 'border-white/10 bg-white/[0.04]'
          : 'border-slate-200 bg-white'
      } ${className}`}
    >
      <Lock className={`w-3 h-3 ${dark ? 'text-white/60' : 'text-slate-500'}`} />
      <span
        className={`text-[11px] font-semibold tracking-wide ${
          dark ? 'text-white/80' : 'text-slate-700'
        }`}
      >
        Secure payment by
      </span>
      <StripeWordmark className="text-[12px]" />
    </div>
  );
};

export default StripeTrustBanner;
